import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { useWindowSize } from "hooks/useWindowSize";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useModal } from "hooks/useModal";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { RowSelectionState } from "react-table-8.10.7";

import Modal from "components/DesignSystem/Modal/Modal";

import BillDeleteModal from "./BillDeleteModal";
import BillMarkAsActiveModal from "./BillMarkAsActiveModal";
import BillUnlinkTransactionsModal from "./BillUnlinkTransactionsModal";
import { VendorBillsFilterType } from "./VendorBillsFilter";

type TableSelectionControlsProps = {
  filter?: VendorBillsFilterType;
  groupId: string;
  entityId: string;
  currentPage: number;
  onSelectionChange?: (billsIds?: string[]) => void;
  selectedBills: string[];
  rowSelection?: RowSelectionState;
  setRowSelection?: (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
};

export type SelectedRow = {
  uuid: string;
  is_recurring_enabled: boolean;
  linked_payroll_id: string;
};

export default function TableSelectionControls({
  groupId,
  entityId,
  onSelectionChange,
  selectedBills,
  rowSelection,
}: TableSelectionControlsProps) {
  const { isMobile } = useWindowSize();
  const markAsActiveModal = useModal();
  const deleteModal = useModal();
  const unlinkTransactionsModal = useModal();

  return (
    <>
      {selectedBills.length > 0 && (
        <>
          {isMobile && (
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.EXPENSE}
              blockedUi={<LockedButton text="Actions" />}
            >
              <Dropdown.Root>
                <Dropdown.Trigger asChild className="t-group">
                  <div>
                    <Button size="small">
                      <span className="t-flex t-items-center t-gap-1">
                        Actions
                        <ArrowDown color="currentColor" size="16" />
                      </span>
                    </Button>
                  </div>
                </Dropdown.Trigger>
                <Dropdown.Portal>
                  <Dropdown.Content side="top" align="end" sideOffset={8}>
                    <Dropdown.Item onSelect={markAsActiveModal.open}>
                      Mark as Active
                    </Dropdown.Item>
                    <Dropdown.Item onSelect={deleteModal.open}>
                      Delete
                    </Dropdown.Item>
                    <Dropdown.Item onSelect={unlinkTransactionsModal.open}>
                      Unlink Transactions
                    </Dropdown.Item>
                  </Dropdown.Content>
                </Dropdown.Portal>
              </Dropdown.Root>
            </LockedUi>
          )}
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.EXPENSE}
            blockedUi={isMobile ? null : <LockedButton text="Mark as Active" />}
          >
            <Modal.Root
              open={markAsActiveModal.isOpen}
              onOpenChange={markAsActiveModal.toggle}
            >
              {!isMobile && (
                <Modal.Trigger asChild>
                  <Button size="small">Mark as Active</Button>
                </Modal.Trigger>
              )}
              <Modal.Content size="xl">
                <BillMarkAsActiveModal
                  groupId={groupId}
                  entityId={entityId}
                  selectedRows={selectedBills}
                  rowSelection={rowSelection}
                  success={() => {
                    markAsActiveModal.close();
                    onSelectionChange?.([]);
                  }}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>

          <LockedUi
            feature={WRITE_PERMISSION_KEYS.EXPENSE}
            blockedUi={isMobile ? null : <LockedButton text="Delete" />}
          >
            <Modal.Root
              open={deleteModal.isOpen}
              onOpenChange={deleteModal.toggle}
            >
              {!isMobile && (
                <Modal.Trigger asChild>
                  <Button size="small">Delete</Button>
                </Modal.Trigger>
              )}
              <Modal.Content size="xl">
                <BillDeleteModal
                  groupId={groupId}
                  entityId={entityId}
                  selectedRows={selectedBills}
                  rowSelection={rowSelection}
                  success={() => {
                    deleteModal.close();
                    onSelectionChange?.([]);
                  }}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>

          <LockedUi
            feature={WRITE_PERMISSION_KEYS.EXPENSE}
            blockedUi={
              isMobile ? null : <LockedButton text="Unlink Transactions" />
            }
          >
            <Modal.Root
              open={unlinkTransactionsModal.isOpen}
              onOpenChange={unlinkTransactionsModal.toggle}
            >
              {!isMobile && (
                <Modal.Trigger asChild>
                  <Button size="small">Unlink Transactions</Button>
                </Modal.Trigger>
              )}
              <Modal.Content size="small">
                <BillUnlinkTransactionsModal
                  groupId={groupId}
                  entityId={entityId}
                  selectedRows={selectedBills}
                  success={() => {
                    unlinkTransactionsModal.close();
                    onSelectionChange?.([]);
                  }}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>
        </>
      )}
    </>
  );
}
